import type { ObjectTypeGroup } from '@/entities/Inventory/ObjectSubTypes'
import type {
  ObjectPassportFieldDefinition,
  ObjectPassportFieldGroup,
  ObjectPassportFieldProps,
  ObjectPassportFieldTableViewOrder,
} from '@/entities/Inventory/ObjectPassportFields/model/types'
import { resolveFieldInventoryTypeId } from '@/entities/Inventory/ObjectPassportFields/model/objectPassportFieldAdapter'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'

const PASSPORT_GRID_COLS = 12

const numberOrDefault = (value: unknown, fallback: number): number => {
  const num = Number(value)
  return Number.isFinite(num) ? num : fallback
}

const positiveNumberOrNull = (value: unknown): number | null => {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? num : null
}

const viewTypeOrDefault = (value: unknown): 'value' | 'table' => {
  return value === 'table' ? 'table' : 'value'
}

/**
 * Сервер хранит row/column с 1 — как в Passport Builder (`objectTypeFieldLayout`).
 */
function normalizeTableViewOrder(
  tableViewOrder: ObjectPassportFieldTableViewOrder,
): Pick<GridFieldType, 'row' | 'cell' | 'length'> {
  const row = Math.max(0, numberOrDefault(tableViewOrder.row, 1) - 1)
  if (tableViewOrder.view_type === 'table') {
    return { row, cell: 0, length: PASSPORT_GRID_COLS }
  }

  const cell = Math.max(0, numberOrDefault(tableViewOrder.column, 1) - 1)
  const length = Math.max(
    1,
    Math.min(PASSPORT_GRID_COLS, numberOrDefault(tableViewOrder.colspan, 4)),
  )

  return { row, cell, length }
}

function layoutNameForField(
  field: ObjectPassportFieldDefinition,
  tableViewOrder: ObjectPassportFieldTableViewOrder,
): string {
  const custom = tableViewOrder.name?.trim()
  return custom || field.name
}

/**
 * Возвращает только размещения поля с координатами сетки.
 */
function gridPropsForField(field: ObjectPassportFieldDefinition): ObjectPassportFieldProps[] {
  return field.field_props.filter((props) => props.table_view_order != null)
}

/**
 * Одна ячейка грида из `payloads.fields[]` + `field_props.table_view_order`.
 */
export function objectPassportFieldToGridItem(
  field: ObjectPassportFieldDefinition,
  fieldProps = gridPropsForField(field)[0],
): GridFieldType | null {
  const tableViewOrder = fieldProps?.table_view_order
  if (!tableViewOrder) return null

  const groupId = positiveNumberOrNull(tableViewOrder.group_id)

  return {
    id: String(field.id),
    type: 'field',
    field_layout_id: fieldProps.id,
    inventory_field_id: field.id,
    inventory_type: resolveFieldInventoryTypeId(field),
    palette_source: 'own',
    name: layoutNameForField(field, tableViewOrder),
    ...normalizeTableViewOrder(tableViewOrder),
    view_type: viewTypeOrDefault(tableViewOrder.view_type),
    pseudo_sql: fieldProps.pseudo_sql ?? null,
    ...(groupId != null ? { group_id: groupId } : {}),
  }
}

/**
 * Шаблон грида runtime из GET /objects/:id/passport (`payloads.fields`).
 * Только размещённые поля (`field_props[].table_view_order`), без legacy `passport_grid_template`.
 */
export function objectPassportFieldsToGridTemplate(
  fields: ObjectPassportFieldDefinition[],
): GridFieldType[] {
  return fields
    .flatMap((field) => {
      const placements = gridPropsForField(field)
      return placements.map((fieldProps) => objectPassportFieldToGridItem(field, fieldProps))
    })
    .filter((item): item is GridFieldType => item != null)
    .sort(
      (left, right) =>
        left.row - right.row || left.cell - right.cell || left.name.localeCompare(right.name),
    )
}

export function objectPassportFieldGroupsToRuntimeGroups(
  groups: ObjectPassportFieldGroup[],
): ObjectTypeGroup[] {
  return groups.map((group) => ({
    id: group.id,
    catalog_invent_objects_type_id: group.catalog_invent_objects_type_id,
    name: group.name,
    open: group.open,
    position: group.position === 'PREPEND' ? 'PREPEND' : 'APPEND',
    sort: group.sort,
    is_tab: group.is_tab,
    type: group.type as ObjectTypeGroup['type'],
    list_invent_object_type_id: group.list_invent_object_type_id,
  }))
}
